import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { CheckCircle, ChevronDown, ChevronUp, MessageSquare, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAddFeedback } from '@/api/campaigns/add-feedback';
import { InterviewIntroduction } from '@/components/interview/interview-introduction.tsx';
import { InterviewFeedback } from '@/components/interview/interview-feedback.tsx';

export const Route = createFileRoute('/feedbacks/$campaignType/$campaignId')({
  component: FeedbackPage
});

function FeedbackPage() {
  const { campaignId, campaignType } = Route.useParams();
  const addFeedback = useAddFeedback(campaignId);

  // Form states - declare all hooks before any returns
  const [simpleInput, setSimpleInput] = useState('');
  const [showFullForm, setShowFullForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  // Success state
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Interview introduction state
  const [isInterviewReady, setIsInterviewReady] = useState(false);

  // Render Interview introduction first, then Interview component for Interview campaigns
  if (campaignType.toLowerCase() === 'interview') {
    if (!isInterviewReady) {
      return <InterviewIntroduction onReady={() => setIsInterviewReady(true)} />;
    }
    return <InterviewFeedback campaignId={campaignId} />;
  }

  // Otherwise render the Form component (existing code below)

  // Validation states
  const isSimpleFormValid = simpleInput.trim().length > 0;
  const isStructuredFormValid = description.trim().length > 0 || title.trim().length > 0;

  const handleSubmitFeedback = (isSimpleMode: boolean) => {
    let feedbackData: { title?: string; content: string; category?: string };

    if (isSimpleMode) {
      if (!isSimpleFormValid) return;
      feedbackData = {
        content: simpleInput.trim()
      };
      setSimpleInput('');
    } else {
      if (!isStructuredFormValid) return;
      feedbackData = {
        content: description.trim() || title.trim(),
        title: title.trim() || undefined,
        category: category.trim() || undefined
      };
      setTitle('');
      setDescription('');
      setCategory('');
    }

    addFeedback.mutate(feedbackData, {
      onSuccess: () => {
        setIsSubmitted(true);
        toast.success('Merci pour votre feedback !', {
          description: 'Votre retour a été enregistré avec succès.'
        });
      },
      onError: () => {
        toast.error('Erreur', {
          description: "Une erreur est survenue lors de l'envoi du feedback."
        });
      }
    });
  };

  const handleSubmitAnother = () => {
    setIsSubmitted(false);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-3 bg-linear-to-br from-green-500 to-emerald-500 rounded-lg">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Merci !</CardTitle>
            <CardDescription>
              Votre feedback a été enregistré avec succès. Nous apprécions votre contribution.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="hero" className="w-full" onClick={handleSubmitAnother}>
              Soumettre un autre feedback
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Input Section - Centered Vertically */}
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="container mx-auto max-w-3xl space-y-8">
          {/* Header */}
          <div className="text-center space-y-4 animate-fade-in-up">
            <div className="flex justify-center">
              <div className="p-3 bg-linear-to-br from-primary to-secondary rounded-lg">
                <Sparkles className="h-8 w-8 text-primary-foreground" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">
              Partagez votre{' '}
              <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">feedback</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Votre avis compte ! Partagez vos idées et suggestions pour nous aider à améliorer.
            </p>
          </div>

          {/* Simple Input or Full Form */}
          <Card className="border-2 animate-fade-in overflow-hidden" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-6 space-y-4">
              {!showFullForm ? (
                // Simple AI-style input
                <div className="space-y-4">
                  <div className="relative">
                    <Textarea
                      placeholder="Décrivez votre idée ou suggestion..."
                      rows={3}
                      value={simpleInput}
                      onChange={(e) => setSimpleInput(e.target.value)}
                      className="pr-14 resize-none text-base"
                    />
                    <Button
                      size="icon"
                      variant="hero"
                      className="absolute bottom-3 right-3 h-10 w-10 rounded-full"
                      onClick={() => handleSubmitFeedback(true)}
                      disabled={!isSimpleFormValid || addFeedback.isPending}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-start">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFullForm(true)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <ChevronDown className="mr-2 h-4 w-4" />
                      Formulaire structuré
                    </Button>
                  </div>
                </div>
              ) : (
                // Full structured form
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">Formulaire structuré</h3>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFullForm(false)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="mr-2 h-4 w-4" />
                      Mode simple
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Titre</label>
                    <Input
                      placeholder="Une courte description de votre feedback..."
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Description détaillée</label>
                    <Textarea
                      placeholder="Expliquez votre idée en détail..."
                      rows={4}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Catégorie (optionnel)</label>
                    <Input
                      placeholder="Fonctionnalité, Design, Performance..."
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="hero"
                      className="flex-1"
                      onClick={() => handleSubmitFeedback(false)}
                      disabled={!isStructuredFormValid || addFeedback.isPending}
                    >
                      {addFeedback.isPending ? 'Envoi en cours...' : 'Envoyer le feedback'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
